import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api, setToken, clearToken } from '@/lib/api';
import { R } from '@/lib/routes';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, let AuthCallback exchange the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((data) => {
    if (data.token) setToken(data.token);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch {}
    clearToken();
    setUser(null);
    window.location.href = R.login;
  }, []);

  // Root-Admin roleplay: start/stop impersonation, then hard-reload so every
  // module gate, menu and permission re-evaluates against the effective user.
  const startImpersonation = useCallback(async (targetUserId) => {
    await api.post('/auth/impersonate', { target_user_id: targetUserId });
    window.location.assign(R.hub);
  }, []);

  const stopImpersonation = useCallback(async () => {
    await api.post('/auth/impersonate/stop');
    window.location.assign(R.hub);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, login, logout, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
